import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3Icon, HandCoinsIcon, RecycleIcon, SearchIcon, SparklesIcon } from 'lucide-react';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
const MAIN_BENEFITS = [{
  label: 'See what works',
  detail: 'Across portfolios, countries and channels.',
  icon: BarChart3Icon,
  image: 'https://cdn.magicpatterns.com/uploads/kAPX1sS6y8zcjhhHD6CLzo/Screenshot_2026-09-21_at_13.14.09.png',
  alt: 'Portfolio overview comparing opportunity by market and product'
}, {
  label: 'Learn faster',
  detail: 'Apply success from one market to another.',
  icon: SparklesIcon,
  image: 'https://cdn.magicpatterns.com/uploads/3feRkRv9AX1mR6BMuNMFrM/Screenshot_2026-09-21_at_13.13.56.png',
  alt: 'Portfolio view showing performance across assessed markets'
}] as const;
const SUPPORTING_BENEFITS = [{
  label: 'Reuse more',
  detail: 'Adapt instead of rebuild.',
  icon: RecycleIcon
}, {
  label: 'Spot opportunities',
  detail: 'Identify where proven ideas can travel.',
  icon: SearchIcon
}, {
  label: 'Build buying power',
  detail: 'More volume creates lower unit cost.',
  icon: HandCoinsIcon
}] as const;
export function SlideSharedAdvantage({}: SlideProps) {
  return <InteriorSlide banner={<SlideBanner data-id="element-112" data-mp="1juk7s81ykew9">
          Centralise the visibility, learning and buying power — not every
          decision.
        </SlideBanner>} data-id="element-111" data-mp="2gme03dhn8zhc">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="flex flex-shrink-0 flex-col gap-[clamp(0.5rem,1vw,1rem)] lg:flex-row lg:items-end lg:justify-between lg:gap-10" data-id="element-113" data-mp="1otdnm74pwhzl">
        <div className="min-w-0" data-id="element-114" data-mp="3492p0j52ls7g">
          <p className="mb-2 text-[clamp(0.8rem,1.15vw,1.05rem)] font-semibold italic leading-snug text-[var(--muted)]" data-id="element-115" data-mp="34c8dbw5vkq24">
            “Snakker seems to be popping up everywhere.”
          </p>
          <h2 className="text-[clamp(1.7rem,3.2vw,2.9rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-116" data-mp="198si049yqkkx">
            Good. Now let’s make that{' '}
            <span className="text-[var(--brand)]" data-id="element-117" data-mp="1j3kcv0c5utc0">activity compound.</span>
          </h2>
        </div>
        <p className="max-w-sm flex-shrink-0 border-l-2 border-[var(--brand)] pl-4 text-[clamp(0.8rem,1.05vw,0.98rem)] font-semibold leading-snug text-[var(--fg)] lg:border-l-0 lg:border-r-2 lg:pl-0 lg:pr-4 lg:text-right" data-id="element-118" data-mp="1e6bw946nuxcv">
          A central overview gives your team something individual campaigns cannot.
        </p>
      </motion.header>

      <div className="mt-[clamp(0.8rem,1.6vw,1.4rem)] grid min-h-0 flex-1 grid-cols-1 gap-[clamp(0.6rem,1.2vw,1.1rem)] lg:grid-cols-2" data-id="element-119" data-mp="vfayukl6v9vf">
        {MAIN_BENEFITS.map((benefit, index) => <motion.figure key={benefit.label} initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.18 + index * 0.1,
        duration: 0.5
      }} className="flex min-h-0 flex-col rounded-2xl bg-[var(--surface)] p-[clamp(0.7rem,1.2vw,1.1rem)]" data-id="element-120" data-mp="7ndmmsdtufts" data-mpi={index}>
            <figcaption className="flex flex-shrink-0 items-center gap-[clamp(0.5rem,0.9vw,0.8rem)]" data-id="element-121" data-mp="25du6dvus0ad1">
              <span className="flex h-[clamp(1.9rem,2.4vw,2.4rem)] w-[clamp(1.9rem,2.4vw,2.4rem)] flex-shrink-0 items-center justify-center rounded-xl bg-[var(--brand)]" data-id="element-122" data-mp="29e4f3g9ydevl">
                <benefit.icon className="h-[clamp(0.9rem,1.2vw,1.15rem)] w-[clamp(0.9rem,1.2vw,1.15rem)] text-white" aria-hidden="true" data-id="element-123" data-mp="323l0htz5nvib" />
              </span>
              <span className="min-w-0" data-id="element-124" data-mp="2t2mxnf0gdu1b">
                <span className="block text-[clamp(0.95rem,1.4vw,1.35rem)] font-extrabold uppercase leading-tight tracking-[-0.02em] text-[var(--fg)]" data-id="element-125" data-mp="2t1bt3cz6oi95">
                  {benefit.label}
                </span>
                <span className="block text-[clamp(0.72rem,0.98vw,0.92rem)] leading-snug text-[var(--muted)]" data-id="element-126" data-mp="1hp199w2f3wgu">
                  {benefit.detail}
                </span>
              </span>
            </figcaption>

            <div className="mt-[clamp(0.5rem,0.9vw,0.8rem)] flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-xl bg-white p-1.5 ring-1 ring-[var(--fg)]/10" data-id="element-127" data-mp="1s4jzd1r08b2t">
              <img src={benefit.image} alt={benefit.alt} className="max-h-full w-full rounded-lg object-contain" data-id="element-128" data-mp="1j7jeh5358v44" />
            </div>
          </motion.figure>)}
      </div>

      <ul className="mt-[clamp(0.6rem,1.1vw,1rem)] grid flex-shrink-0 grid-cols-1 gap-[clamp(0.5rem,1vw,0.9rem)] sm:grid-cols-3" data-id="element-129" data-mp="26vj6078a4pjr">
        {SUPPORTING_BENEFITS.map((benefit, index) => <motion.li key={benefit.label} initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.42 + index * 0.08,
        duration: 0.45
      }} className="flex items-center gap-[clamp(0.5rem,0.9vw,0.8rem)] rounded-2xl bg-[var(--surface)] px-[clamp(0.7rem,1.2vw,1.1rem)] py-[clamp(0.5rem,0.9vw,0.8rem)]" data-id="element-130" data-mp="21ysl96lj55s0" data-mpi={index}>
            <benefit.icon className="h-[clamp(1rem,1.3vw,1.25rem)] w-[clamp(1rem,1.3vw,1.25rem)] flex-shrink-0 text-[var(--brand)]" aria-hidden="true" data-id="element-131" data-mp="371d1omiema5i" />
            <span className="min-w-0" data-id="element-132" data-mp="3fy5ings4dv55">
              <span className="block text-[clamp(0.78rem,1.05vw,1rem)] font-extrabold uppercase leading-tight tracking-[-0.01em] text-[var(--fg)]" data-id="element-133" data-mp="3kvnogyaubu8x">
                {benefit.label}
              </span>
              <span className="block text-[clamp(0.68rem,0.92vw,0.85rem)] leading-snug text-[var(--muted)]" data-id="element-134" data-mp="3ut210mtn7yvy">
                {benefit.detail}
              </span>
            </span>
          </motion.li>)}
      </ul>
    </InteriorSlide>;
}